import * as React from 'react'
import FBLogin from 'react-facebook-login/dist/facebook-login-render-props'
import { GoogleLogin as DeprecatedGoogleLogin } from 'react-google-login'
import { useApolloClient } from '@apollo/react-hooks'
import { useGoogleLogin } from '@react-oauth/google'

import { analytics } from '~/analytics'
import { socialLoginMutation } from '~/gql/mutations'
import useIsMobile from '~/hooks/use-is-mobile'
import useVendor from '~/hooks/use-vendor'
import notifier from '~/truck/notifier'
import { sleep } from '~/utils'
import callbackRedirection, { saveSession } from '~/utils/after-auth'
import { loginProviders, timeouts } from '~/utils/constants'

import SocialLoginButton from '../social-login-button'

import { Container } from './elements'

const renderFacebookButton = props => {
  const { onClick, isSdkLoaded } = props

  return <SocialLoginButton provider={loginProviders.FACEBOOK} onClick={onClick} disabled={!isSdkLoaded} />
}

const GoogleLoginButton = ({ onSocialLogin }) => {
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: tokenResponse => {
      onSocialLogin(loginProviders.GOOGLE, tokenResponse.code)
    },
    flow: 'auth-code',
  })

  return <SocialLoginButton provider={loginProviders.GOOGLE} onClick={handleGoogleLogin} />
}

const SocialLoginButtons = props => {
  const { style, className } = props

  const apolloClient = useApolloClient()

  const vendor = useVendor()

  const isMobile = useIsMobile()

  const onSocialLogin = async (provider, token) => {
    const options = {
      mutation: socialLoginMutation,
      variables: { provider, token },
    }

    try {
      const response = await apolloClient.mutate(options)

      analytics.login(provider)

      await sleep(timeouts.REDIRECTION)

      saveSession(response.data.socialLogin)

      callbackRedirection()
    } catch (error) {
      const message = error?.graphQLErrors?.[0]?.message

      if (message) {
        notifier.error(message, { timeout: 5 * 1000 })
      }
    }
  }

  const onFacebookLogin = data => {
    return onSocialLogin(loginProviders.FACEBOOK, data.accessToken)
  }

  if (!vendor.facebookAppId && !vendor.googleClientId) {
    return null
  }

  return (
    <Container style={style} className={className}>
      {vendor.facebookAppId && (
        /*
         * We need to disabled redirect on mobile.
         *
         * There is an issue for this:
         * https://github.com/keppelen/react-facebook-login/issues/175
         *
         * `disableMobileRedirect` is an undocumented prop that solves it:
         * https://github.com/keppelen/react-facebook-login/issues/257
         * https://github.com/keppelen/react-facebook-login/pull/192
         */
        <FBLogin
          appId={vendor.facebookAppId}
          fields="first_name,last_name,email"
          isMobile={isMobile}
          callback={onFacebookLogin}
          render={renderFacebookButton}
          disableMobileRedirect
        />
      )}
      {/* TODO:  Remove this component after migration of all vendors that use google authentication */}
      {vendor.googleClientId && !vendor.googleClientSecret && (
        <DeprecatedGoogleLogin
          clientId={vendor.googleClientId}
          cookiePolicy="single_host_origin"
          onSuccess={data => onSocialLogin(loginProviders.GOOGLE, data.tokenId)}
          render={props => (
            <SocialLoginButton provider={loginProviders.GOOGLE} onClick={props.onClick} disabled={props.disabled} />
          )}
        />
      )}
      {vendor.googleClientId && vendor.googleClientSecret && <GoogleLoginButton onSocialLogin={onSocialLogin} />}
    </Container>
  )
}

export default SocialLoginButtons
